import type { EstadoCartaoKanban, KanbanCartao, KanbanHistoricoItem, NivelPrioridadeKanban, RegraCompatibilidade, TipoBanho, TipoTinta } from '@pieltec/contracts';
import { rotuloBanho, rotuloTinta } from '../material-atributos';
import type { StatusTone } from '../../components/ui/status-indicator';

export function rotuloEstado(estado: EstadoCartaoKanban) {
  switch (estado) {
    case 'EM_PRODUCAO':
      return { texto: 'Em produção', icone: 'CircleDot', tone: 'info' as StatusTone, classes: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'AGUARDANDO':
      return { texto: 'Aguardando', icone: 'Clock', tone: 'neutral' as StatusTone, classes: 'bg-slate-50 text-slate-700 border-slate-200' };
    case 'PAUSADO':
      return { texto: 'Pausado', icone: 'PauseCircle', tone: 'warning' as StatusTone, classes: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'BLOQUEADO':
      return { texto: 'Bloqueado', icone: 'Ban', tone: 'danger' as StatusTone, classes: 'bg-rose-50 text-rose-700 border-rose-200' };
    case 'RETRABALHO':
      return { texto: 'Retrabalho', icone: 'RotateCcw', tone: 'warning' as StatusTone, classes: 'bg-purple-50 text-purple-700 border-purple-200' };
    case 'CONCLUIDO':
      return { texto: 'Concluído', icone: 'CheckCircle2', tone: 'success' as StatusTone, classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  }
}

export function faixaUrgencia({ nivel, prazo }: { nivel: NivelPrioridadeKanban; prazo: 'ok' | 'perto' | 'estourado' }) {
  if (prazo === 'estourado') return 'bg-rose-600';
  if (nivel === 'urgente') return 'bg-rose-500';
  if (nivel === 'alta' || prazo === 'perto') return 'bg-amber-500';
  return 'bg-slate-300';
}

export function iconeUrgencia(nivel: NivelPrioridadeKanban, prazo: 'ok' | 'perto' | 'estourado') {
  if (prazo === 'estourado') return 'CalendarX';
  if (nivel === 'urgente') return 'AlertTriangle';
  if (nivel === 'alta') return 'ArrowUp';
  if (prazo === 'perto') return 'Clock';
  return null;
}

export function formatarProgresso(produzido: number, alvo: number): string {
  return `${produzido}/${alvo}`;
}

export function formatarPrazo(iso: string): string {
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}`;
}

/** Prazo completo dd/mm/aaaa (painel do cartão). Vazio ou data inválida vira 'Sem prazo'. */
export function formatarPrazoCompleto(iso: string | null | undefined): string {
  if (!iso) return 'Sem prazo';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Sem prazo';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${d.getFullYear()}`;
}

/** Texto 'Compatíveis: E1, E2' do cartão; null quando não há cabine compatível a mostrar. */
export function textoCabinesCompativeis(cabines: readonly string[] | null | undefined): string | null {
  if (!cabines || cabines.length === 0) return null;
  return `Compatíveis: ${cabines.join(', ')}`;
}

const ROTULO_REGRA: Record<RegraCompatibilidade, string> = {
  TINTA: 'tinta não atendida por esta cabine',
  COR: 'cor não atendida por esta cabine',
  PORTE: 'porte acima do máximo desta cabine',
  ISOLAMENTO: 'isolamento não aceito por esta cabine',
};

export function rotuloRegraFalha(regra: RegraCompatibilidade): string {
  return ROTULO_REGRA[regra];
}

export function mensagemCabineIncompativel(regras: readonly RegraCompatibilidade[]): string {
  return `Cabine incompatível: ${regras.map((regra) => rotuloRegraFalha(regra)).join('; ')}.`;
}

export function chipsSubtipo(cartao: { tipoBanho: TipoBanho | null; tipoTinta: TipoTinta | null }): string[] {
  return [rotuloBanho(cartao.tipoBanho), rotuloTinta(cartao.tipoTinta)].filter((chip): chip is string => chip !== null);
}

/** Selo "aberta com peças à frente" (Fase 19): quantas peças da etapa já estão na etapa seguinte. Texto sempre visível. */
export function rotuloPecasAFrente(cartao: Pick<KanbanCartao, 'estado'> & { quantidadeAFrente?: number }): string | null {
  const quantidade = cartao.quantidadeAFrente ?? 0;
  if (quantidade <= 0 || cartao.estado === 'CONCLUIDO') return null;
  return `${quantidade} já na próxima etapa`;
}

export function ehRegistroTardio(item: Pick<KanbanHistoricoItem, 'eventType'>): boolean {
  return item.eventType === 'kanban.apontamento_tardio';
}

/** D-03: quem cobriu o registro tardio (célula que apontou no lugar da etapa pulada). */
export function origemDoRegistroTardio(item: Pick<KanbanHistoricoItem, 'eventType' | 'after'>): string | null {
  if (!ehRegistroTardio(item)) return null;
  const depois = item.after;
  if (depois === null || typeof depois !== 'object') return null;
  const celula = (depois as Record<string, unknown>).origemCelula;
  return typeof celula === 'string' && celula.length > 0 ? `Registrado pela célula ${celula}` : null;
}

/** D-04: filtro local "Só registros tardios"; com o filtro desligado devolve a própria lista. */
export function filtrarHistoricoTardio<T extends Pick<KanbanHistoricoItem, 'eventType'>>(itens: readonly T[], apenasTardios: boolean): readonly T[] {
  return apenasTardios ? itens.filter(ehRegistroTardio) : itens;
}
